import React, { memo, useEffect } from 'react';

import { useSelector } from 'react-redux';

import dynamic from 'next/dynamic';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';
import { ClientOnly } from '@atc/react-client-only';

import { Heading } from 'reaxl';

import getPathSRP from '@/utilities/getPathSRP';
import usePaidSearchCookies from '@/utilities/usePaidSearchCookies';

import queryDuck from '@/ducks/queryDuck.js';
import vdpActiveInteractionDuck from '@/ducks/vdp/vdpActiveInteractionDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import useVdpEsntialEligibility from '@/hooks/useVdpEsntialEligibility';

import MyWalletContainer from '@/containers/MyWalletContainer';
import OptimizelyManualTriggerContainer from '@/containers/OptimizelyManualTriggerContainer';

import CalculateStrongAttributesContainer from './CalculateStrongAttributesContainer';
import TrackPrintContainer from './TrackPrintContainer';
import VDPHeaderContainer from './VDPHeaderContainer';

const VehicleDetailsContainer = dynamic(() => import(
    /* webpackChunkName: "VehicleDetailsContainer" */
    '@/containers/vdp/VehicleDetailsContainer'
), { ssr: true });

const CompareListingsContainer = dynamic(() => import(
    /* webpackChunkName: "CompareListingsContainer" */
    '@/containers/CompareListingsContainer'
), { ssr: false });

const ShareListingsContainer = dynamic(() => import(
    /* webpackChunkName: "ShareListingsContainer" */
    '@/containers/ShareListingsContainer'
), { ssr: false });

function VehicleDetailsPageContainer() {
    const { id: listingId } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const queryList = useSelector(queryDuck.selectors.getDuckState);
    const showCompareModal = useSelector(vdpActiveInteractionDuck.selectors.getShowCompareModal);

    const { brand } = useBrand();

    const {
        compare: [showCompare],
        my_atc_shareable_link: [enableSharableLink],
        my_wallet: [enableMyWallet],
    } = useFeatures([
        'compare',
        'my_atc_shareable_link',
        'my_wallet',
    ]);

    useEffect(() => {
        if (!listingId) {
            const query = { ...queryList };
            delete query.referrer;
            delete query.clickType;
            delete query.listingId;

            // if no expired param append them
            if (!Object.keys(query).includes('redirectExpiredPage')) {
                query.redirectExpiredPage = 1;
            }

            // TODO: BONNET NEXT - this generates a whole URL so it needs the basePath
            // should this be navigating via the nextjs router
            const goToSRP = async () => getPathSRP(query, { brand, basePath: true });
            goToSRP().then((urlGoTo) => window.location.assign(urlGoTo));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    usePaidSearchCookies();

    const isEcommHyundai = queryList?.experience === 'hyundai-buy-online';
    const { isEligible: isEsntialEligible, isLoading: isEsntialEligibilityLoading } = useVdpEsntialEligibility(isEcommHyundai);

    return (
        <>
            {listingId ? (
                <main aria-labelledby="vehicle-details-heading">
                    <VehicleDetailsContainer
                        isEsntialEligible={isEsntialEligible}
                        isEsntialEligibilityLoading={isEsntialEligibilityLoading}
                    />
                </main>
            ) : (
                <main aria-labelledby="vehicle-details-expired-heading">
                    <Heading
                        className="margin-vertical-7 margin-horizontal-7 text-center"
                        id="vehicle-details-expired-heading"
                    >
                        This car is no longer available. One moment while we take you to the search results page.
                    </Heading>
                </main>
            )}

            <TrackPrintContainer />

            <VDPHeaderContainer />

            {enableMyWallet && <MyWalletContainer />}

            {enableMyWallet && showCompare && showCompareModal && (
                <ClientOnly>
                    <CompareListingsContainer />
                </ClientOnly>
            )}

            {enableSharableLink && (
                <ClientOnly>
                    <ShareListingsContainer />
                </ClientOnly>
            )}

            <CalculateStrongAttributesContainer />
            <OptimizelyManualTriggerContainer features={['extended_hybrid_vdp']} />
        </>
    );
}

export default memo(VehicleDetailsPageContainer);
